import { Box, Link } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import React, { FC } from 'react'
import { useNavigate } from 'react-router-dom'

import { EmsProductTypeValue } from 'src/Routes/Installation/InstallationModel'
import { getEmsProductImage } from 'src/Libs/installations/utils/product-image'
import { useDispatch } from 'react-redux'
import { setActiveStorage } from 'src/State/Storage/StorageActions'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { MasterDeviceType } from '../types'
import G1Nowifi from '@e3dc-react/icons/icons/components/G1Nowifi'
import { Installation } from 'src/Libs/installations'
import { FarmIcon } from '@e3dc-react/icons'

const useStyles = makeStyles()(theme => ({
  card: {
    padding: theme.spacing(2),
    background: theme.palette.shades.shade04,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.shades.shade03}`,
    position: 'relative',
    height: '100%',
    overflow: 'hidden',
  },
  productImg: {
    marginTop: theme.spacing(2),
    width: '85px',
  },
  link: {
    textDecoration: 'none',
    cursor: 'pointer',
  },
  deletedItem: {
    borderColor: theme.shape.errorBanner?.background,
  },
  deletedItemOverlay: {
    backgroundColor: theme.shape.errorBanner?.background + '85' /* adding alpha to hex code for opacity */,
    position: 'absolute',
    left: 0,
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    height: '33%',
    textTransform: 'uppercase',
    color: theme.palette.error.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: theme.typography.fontWeightMedium,
  },
  cornerContainer: {
    position: 'absolute',
    bottom: theme.spacing(1),
    right: theme.spacing(1),
    width: theme.spacing(4),
    display: 'flex',
  },
}))

interface SystemCardProps {
  installation: Installation | undefined
  id: string
  type?: EmsProductTypeValue
  isDeleted?: boolean
  isOffline?: boolean
  masterDeviceType?: MasterDeviceType
}

/**
 * This component represents card for each system of selected installation
 * @returns SystemCard Component
 **/
const SystemCard: FC<SystemCardProps> = ({ installation, id, type, isDeleted, masterDeviceType, isOffline }) => {
  const { classes, cx } = useStyles()
  const { t } = useCustomTranslation()
  const productImage = getEmsProductImage(type, installation?.installationStatus, '3D')
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const onClick = (): void => {
    if (!installation || !id) return
    dispatch(setActiveStorage(id))
    navigate(`/overview/installations/${installation?.id}/${id}/details`)
  }

  return (
    <Link onClick={onClick} className={classes.link}>
      <Box className={cx(classes.card, { [classes.deletedItem]: isDeleted })}>
        {isDeleted && <Box className={classes.deletedItemOverlay}>{t('system-details.deleted.label')}</Box>}
        {isOffline && !isDeleted && (
          <Box sx={theme => ({ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, backgroundColor: theme.palette.grey.grey05 + 50 })}>
            <G1Nowifi
              sx={theme => ({
                position: 'absolute',
                top: theme.spacing(3),
                right: theme.spacing(2),
                zIndex: theme.zIndex.appBar - 1,
                color: theme.shape.snackbar.backgroundColor.errorLight,
                fontSize: 30,
                backgroundColor: theme.palette.error.main,
                borderRadius: theme.spacing(4),
              })}
            />
          </Box>
        )}
        {id}
        {masterDeviceType === MasterDeviceType.PHYSICAL && (
          <Box className={classes.cornerContainer}>
            <FarmIcon />
          </Box>
        )}
        {masterDeviceType === MasterDeviceType.VIRTUAL ? (
          <Box className={classes.productImg}>
            <FarmIcon />
          </Box>
        ) : (
          <Box component={'img'} src={productImage} alt="product" className={classes.productImg} />
        )}
      </Box>
    </Link>
  )
}

export default SystemCard
